import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import { ChefHat, ArrowLeft, Globe } from "lucide-react";
import { login } from "@/lib/api";
import { kitchenTranslations } from "@/lib/translations";

export default function KitchenLogin() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  // Default to Spanish
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem("kitchenLanguage") || "es";
  });

  const t = kitchenTranslations[language];

  const toggleLanguage = () => {
    const newLang = language === "es" ? "en" : "es";
    setLanguage(newLang);
    localStorage.setItem("kitchenLanguage", newLang);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!username.trim() || !password.trim()) {
      toast.error(t.enterCredentials);
      return;
    }

    try {
      setLoading(true);
      const res = await login(username, password);
      
      if (res.data.success && res.data.role === "kitchen") {
        sessionStorage.setItem("kitchenAuth", "true");
        toast.success(t.loginSuccess);
        navigate("/kitchen/dashboard");
      } else {
        toast.error(t.invalidCredentials);
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error(t.loginError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background texture-overlay flex items-center justify-center px-4">
      <div className="relative z-10 w-full max-w-md">
        <div className="flex justify-between items-center mb-6">
          <Link to="/">
            <Button variant="ghost" size="sm" data-testid="back-to-home-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t.backToHome}
            </Button>
          </Link>
          
          {/* Language Toggle */}
          <Button
            variant="outline"
            size="sm"
            onClick={toggleLanguage}
            className="border-border"
            data-testid="kitchen-language-toggle"
          >
            <Globe className="w-4 h-4 mr-2" />
            {language === "es" ? "English" : "Español"}
          </Button>
        </div>

        <Card className="bg-card border-border shadow-card" data-testid="kitchen-login-card">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center">
              <ChefHat className="w-8 h-8 text-primary" />
            </div>
            <CardTitle className="font-display text-2xl">{t.kitchenPortal}</CardTitle>
            <CardDescription>{t.loginDescription}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">{t.username}</Label>
                <Input
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder={t.usernamePlaceholder}
                  className="bg-background/50"
                  autoComplete="username"
                  data-testid="kitchen-username-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">{t.password}</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t.passwordPlaceholder}
                  className="bg-background/50"
                  autoComplete="current-password"
                  data-testid="kitchen-password-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-primary hover:bg-primary/90"
                data-testid="kitchen-login-btn"
              >
                {loading ? t.loggingIn : t.loginButton}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
